/**
 * Sprint Database Operations
 *
 * CRUD operations for sprint management.
 */

import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';
import { handleNotFoundError, buildUpdatePayload } from './helpers';

export type SprintStatus = 'planned' | 'active' | 'completed';

export interface Sprint {
  id: string;
  name: string;
  spaceId: string;
  description: string | null;
  goal: string | null;
  startDate: number | null;
  endDate: number | null;
  status: SprintStatus;
  velocity: number | null;
  retrospectiveNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SprintSummary extends Sprint {
  totalTasks: number;
  completedTasks: number;
  totalStoryPoints: number;
  completedStoryPoints: number;
}

export interface CreateSprintData {
  name: string;
  spaceId: string;
  description?: string;
  goal?: string;
  startDate?: number | null;
  endDate?: number | null;
  status?: SprintStatus;
}

export interface UpdateSprintData {
  name?: string;
  description?: string;
  goal?: string;
  startDate?: number | null;
  endDate?: number | null;
  status?: SprintStatus;
  velocity?: number | null;
  retrospectiveNotes?: string | null;
}

// Supabase row type for sprints table
interface SupabaseSprintRow {
  id: string;
  name: string;
  space_id: string;
  description: string | null;
  goal: string | null;
  start_date: number | null;
  end_date: number | null;
  status: SprintStatus;
  velocity: number | null;
  retrospective_notes: string | null;
  created_at: string;
  updated_at: string;
}

// Supabase row type for sprint_summary view
interface SupabaseSprintSummaryRow extends SupabaseSprintRow {
  total_tasks: number;
  completed_tasks: number;
  total_story_points: number;
  completed_story_points: number;
}

// Convert from Supabase (snake_case) to Sprint (camelCase)
function convertSprintFromSupabase(row: SupabaseSprintRow): Sprint {
  return {
    id: row.id,
    name: row.name,
    spaceId: row.space_id,
    description: row.description,
    goal: row.goal,
    startDate: row.start_date,
    endDate: row.end_date,
    status: row.status,
    velocity: row.velocity,
    retrospectiveNotes: row.retrospective_notes,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function convertSprintSummaryFromSupabase(row: SupabaseSprintSummaryRow): SprintSummary {
  return {
    ...convertSprintFromSupabase(row),
    totalTasks: row.total_tasks || 0,
    completedTasks: row.completed_tasks || 0,
    totalStoryPoints: row.total_story_points || 0,
    completedStoryPoints: row.completed_story_points || 0,
  };
}

/**
 * Get all sprints for a space
 */
export async function getSprintsBySpace(spaceId: string): Promise<Sprint[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('sprints')
      .select('*')
      .eq('space_id', spaceId)
      .order('start_date', { ascending: true })
  );

  if (error) throw error;
  return (data || []).map(convertSprintFromSupabase);
}

/**
 * Get sprints with summary (task counts, story points)
 */
export async function getSprintSummaries(spaceId: string): Promise<SprintSummary[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('sprint_summary')
      .select('*')
      .eq('space_id', spaceId)
      .order('start_date', { ascending: true })
  );

  if (error) throw error;
  return (data || []).map(convertSprintSummaryFromSupabase);
}

/**
 * Get a single sprint by ID
 */
export async function getSprint(id: string): Promise<Sprint | null> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('sprints')
      .select('*')
      .eq('id', id)
      .single()
  );

  if (error) return handleNotFoundError(error, null);
  return convertSprintFromSupabase(data);
}

/**
 * Get the currently active sprint for a space
 */
export async function getActiveSprint(spaceId: string): Promise<Sprint | null> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('sprints')
      .select('*')
      .eq('space_id', spaceId)
      .eq('status', 'active')
      .single()
  );

  if (error) return handleNotFoundError(error, null);
  return convertSprintFromSupabase(data);
}

/**
 * Create a new sprint
 */
export async function createSprint(data: CreateSprintData): Promise<Sprint> {
  const insertData = {
    name: data.name,
    space_id: data.spaceId,
    description: data.description || null,
    goal: data.goal || null,
    start_date: data.startDate || null,
    end_date: data.endDate || null,
    status: data.status || 'planned',
  };

  const { data: created, error } = await withRetry(() =>
    supabase
      .from('sprints')
      .insert(insertData)
      .select()
      .single()
  );

  if (error || !created) throw error || new Error('Failed to create sprint');
  return convertSprintFromSupabase(created);
}

/**
 * Update a sprint
 */
export async function updateSprint(id: string, updates: UpdateSprintData): Promise<Sprint> {
  const updatePayload = buildUpdatePayload(updates as Record<string, unknown>, {
    name: 'name',
    description: 'description',
    goal: 'goal',
    startDate: 'start_date',
    endDate: 'end_date',
    status: 'status',
    velocity: 'velocity',
    retrospectiveNotes: 'retrospective_notes',
  });

  const { data, error } = await withRetry(() =>
    supabase
      .from('sprints')
      .update(updatePayload)
      .eq('id', id)
      .select()
      .single()
  );

  if (error || !data) throw error || new Error('Failed to update sprint');
  return convertSprintFromSupabase(data);
}

/**
 * Delete a sprint
 */
export async function deleteSprint(id: string): Promise<boolean> {
  const { error } = await withRetry(() =>
    supabase
      .from('sprints')
      .delete()
      .eq('id', id)
  );

  if (error) throw error;
  return true;
}

/**
 * Start a sprint (set status to active)
 */
export async function startSprint(id: string): Promise<Sprint> {
  // First, check if there's already an active sprint
  const sprint = await getSprint(id);
  if (!sprint) throw new Error('Sprint not found');

  const existingActive = await getActiveSprint(sprint.spaceId);
  if (existingActive && existingActive.id !== id) {
    throw new Error('Another sprint is already active. Complete it first.');
  }

  return updateSprint(id, {
    status: 'active',
    startDate: sprint.startDate || Date.now(),
  });
}

/**
 * Complete a sprint
 */
export async function completeSprint(id: string): Promise<Sprint> {
  return updateSprint(id, {
    status: 'completed',
    endDate: Date.now(),
  });
}

/**
 * Assign a task to a sprint
 */
export async function assignTaskToSprint(taskId: string, sprintId: string | null): Promise<void> {
  const { error } = await withRetry(() =>
    supabase
      .from('tasks')
      .update({ sprint_id: sprintId })
      .eq('id', taskId)
  );

  if (error) throw error;
}

/**
 * Bulk assign tasks to a sprint
 */
export async function bulkAssignTasksToSprint(taskIds: string[], sprintId: string | null): Promise<void> {
  const { error } = await withRetry(() =>
    supabase
      .from('tasks')
      .update({ sprint_id: sprintId })
      .in('id', taskIds)
  );

  if (error) throw error;
}

/**
 * Get tasks in a sprint
 */
export async function getSprintTasks(sprintId: string): Promise<string[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('tasks')
      .select('id')
      .eq('sprint_id', sprintId)
  );

  if (error) throw error;
  return (data || []).map(t => t.id);
}
